import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/Spinner';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'destructive'
  | 'outline'
  | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Show a spinner and disable interaction. `isLoading` kept as an alias. */
  loading?: boolean | undefined;
  isLoading?: boolean | undefined;
  fullWidth?: boolean | undefined;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800',
  secondary:
    'bg-barita-mist/60 text-barita-ink hover:bg-barita-mist active:bg-barita-mist dark:bg-gray-700 dark:text-gray-50 dark:hover:bg-gray-600',
  success: 'bg-success text-white hover:brightness-95 active:brightness-90',
  danger: 'bg-error text-white hover:brightness-95 active:brightness-90',
  destructive: 'bg-error text-white hover:brightness-95 active:brightness-90',
  // Figma action button: white fill, ink hairline border, mist hover.
  outline: 'border border-barita-ink/30 bg-white text-black hover:bg-barita-mist/40 dark:bg-transparent dark:text-foreground dark:hover:bg-gray-800',
  ghost: 'bg-transparent text-foreground hover:bg-barita-mist/40 dark:hover:bg-gray-800',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-small',
  md: 'h-10 px-5 text-base',
  lg: 'h-12 px-6 text-[18px]',
};

/**
 * The single Button primitive. Differences are expressed as `variant`/`size`
 * props — never as forked components or inline overrides. Springy press +
 * focus ring are built in; `loading` shows a spinner and disables interaction.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    loading,
    isLoading,
    fullWidth,
    leftIcon,
    rightIcon,
    disabled,
    children,
    ...props
  },
  ref,
) {
  const busy = loading ?? isLoading ?? false;
  return (
    <button
      ref={ref}
      disabled={disabled ?? busy}
      aria-busy={busy || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center gap-2 rounded-[12px] font-medium',
        'transition-[transform,background-color,box-shadow] duration-fast ease-spring active:scale-[0.98]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {busy ? <Spinner size="sm" /> : leftIcon}
      {children}
      {!busy && rightIcon}
    </button>
  );
});
